<template>
  <div :class="$options.name" class="tw:flex tw:items-center tw:justify-center">
    <ve-progress
      :color="colorProgress"
      :progress="percent"
      :show-percent="false"
      :size="310"
      :angle="0"
      half
      animation="default 0 0"
      hide-legend
      :empty-color="step === null ? '#D1E5FF' : '#F2F2F2'"
      class="circle"
    />
    <div class="tw:flex flex-column tw:items-center tw:gap-3 tw:mt-16" :class="`${$options.name}__container`">
      <template v-if="$slots['center-content']">
        <slot name="center-content" />
      </template>
      <template v-else>
        <p class="tw:heading-2 tw:mt-4 text-center" :class="`${$options.name}__status`">{{ status }}</p>
        <EdroneBadge
          :class="`${$options.name}__industry`"
          :label="`${industryAvgText}: ${industryAverageFormat}`"
          size="small"
        />
        <p class="tw:body-2 text-center tw:!text-default-700" :class="`${$options.name}__description`">
          {{ descriptionText }}
        </p>
        <EdroneButton v-if="!step || step < 4" type="link-text" @click="improve">
          <template #right-icon>
            <font-awesome-icon icon="fa-light fa-arrow-up-right-from-square" />
          </template>
          {{ improveButtonText }}
        </EdroneButton>
      </template>
    </div>
    <EdroneTooltip
      v-if="showTooltip"
      :class="`${$options.name}__howWork`"
      :helper="howWorksButton"
      position="top"
      trigger-event="mouseenter"
      :offset="[0, 4]"
    >
      <font-awesome-icon class="tw:text-default-500" icon="fa-light fa-circle-question" @click="openHowWork" />
    </EdroneTooltip>
    <EdroneTooltip
      type="custom"
      trigger-event="mouseenter"
      position="top"
      :offset="[0, 10]"
      :class="[
        { [`${$options.name}__emoji--noData`]: step === null },
        `${$options.name}__emoji`,
        `${$options.name}__emoji--bottom`,
        `${$options.name}__emoji--${colorEmojiClass}`,
      ]"
    >
      <div>
        <img alt="" src="https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/bottom.png" />
      </div>
      <template #custom>
        <p class="tw:m-0">{{ stepRangeFormat(0) }}</p>
      </template>
    </EdroneTooltip>
    <EdroneTooltip
      type="custom"
      trigger-event="mouseenter"
      position="top"
      :offset="[0, 10]"
      :class="[
        step !== null ? (step < 1 ? `${$options.name}__emoji--noActive` : '') : `${$options.name}__emoji--noData`,
        `${$options.name}__emoji`,
        `${$options.name}__emoji--belowAvg`,
        `${$options.name}__emoji--${colorEmojiClass}`,
      ]"
    >
      <div>
        <img alt="" src="https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/belowAvg.png" />
      </div>
      <template #custom>
        <p class="tw:m-0">{{ stepRangeFormat(1) }}</p>
      </template>
    </EdroneTooltip>
    <EdroneTooltip
      type="custom"
      trigger-event="mouseenter"
      position="top"
      :offset="[0, 10]"
      :class="[
        step !== null ? (step < 2 ? `${$options.name}__emoji--noActive` : '') : `${$options.name}__emoji--noData`,
        `${$options.name}__emoji`,
        `${$options.name}__emoji--average`,
        `${$options.name}__emoji--${colorEmojiClass}`,
      ]"
    >
      <div>
        <img alt="" src="https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/average.png" />
      </div>
      <template #custom>
        <p class="tw:m-0">{{ stepRangeFormat(2) }}</p>
      </template>
    </EdroneTooltip>
    <EdroneTooltip
      type="custom"
      trigger-event="mouseenter"
      position="top"
      :offset="[0, 10]"
      :class="[
        step !== null ? (step < 3 ? `${$options.name}__emoji--noActive` : '') : `${$options.name}__emoji--noData`,
        `${$options.name}__emoji`,
        `${$options.name}__emoji--overAvg`,
        `${$options.name}__emoji--${colorEmojiClass}`,
      ]"
    >
      <div>
        <img alt="" src="https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/overAvg.png" />
      </div>
      <template #custom>
        <p class="tw:m-0">{{ stepRangeFormat(3) }}</p>
      </template>
    </EdroneTooltip>
    <EdroneTooltip
      type="custom"
      trigger-event="mouseenter"
      position="top"
      :offset="[0, 10]"
      :class="[
        step !== null ? (step < 4 ? `${$options.name}__emoji--noActive` : '') : `${$options.name}__emoji--noData`,
        `${$options.name}__emoji`,
        `${$options.name}__emoji--top`,
        `${$options.name}__emoji--${colorEmojiClass}`,
      ]"
    >
      <div>
        <img alt="" src="https://d3vhsxl1pwzf0p.cloudfront.net/indumeter/top.png" />
      </div>
      <template #custom>
        <p class="tw:m-0">{{ stepRangeFormat(4) }}</p>
      </template>
    </EdroneTooltip>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
// @ts-ignore Because there is no type for this package
import { VeProgress } from "vue-ellipse-progress";
import EdroneTooltip from "./EdroneTooltip.vue";
import { gtagSend } from "vue-app/plugins/edroneGtag";
import { $_trans, formatNumberV2 } from "vue-app/utils/functions";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { DTOMetricStep } from "vue-app/api/dto";

export default defineComponent({
  name: "EdroneMeterDetail",
  components: {
    VeProgress,
    EdroneBadge,
    EdroneButton,
    EdroneTooltip,
  },
  props: {
    /**
     * Show emoji based on step
     */
    step: {
      type: null as unknown as PropType<number | null>,
      required: true,
      validator: (value: number) => [0, 1, 2, 3, 4].includes(value) || value === null,
    },
    /**
     * Industry average value
     */
    industryAverage: {
      type: Number,
      required: false,
      default: 0.0,
    },
    /**
     * Industry format number or percentage
     */
    industryFormat: {
      type: String,
      required: false,
      default: "NUMBER",
    },
    /**
     * Steps for next level of indumeter
     */
    steps: {
      type: Array as PropType<DTOMetricStep[]>,
      required: false,
      default: () => [
        { step: 0, minValue: 0, maxValue: 0.2 },
        { step: 1, minValue: 0.2, maxValue: 0.5 },
        { step: 2, minValue: 0.5, maxValue: 0.8 },
        { step: 3, minValue: 0.8, maxValue: 1 },
        { step: 4, minValue: 1, maxValue: 1 },
      ],
    },
    /**
     * Type of metrics whether it is OR, CTR, etc. Needed for GA and translations
     */
    metricName: {
      type: String,
      required: false,
      default: "",
    },
    isRateReversed: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const colorProgress = computed(() => {
      switch (props.step) {
        case 0:
          return "#ff0000";
        case 1:
          return "#ff9f00";
        case 2:
          return "#00c672";
        case 3:
          return "#00c672";
        case 4:
          return "#00c672";
        default:
          return "#00c672";
      }
    });

    const colorEmojiClass = computed(() => {
      switch (props.step) {
        case 0:
          return "bottomColor";
        case 1:
          return "belowAvgColor";
        case 2:
          return "averageColor";
        case 3:
          return "overAvgColor";
        case 4:
          return "topColor";
        default:
          return "noActiveColor";
      }
    });

    const percent = computed(() => {
      switch (props.step) {
        case 0:
          return 0;
        case 1:
          return 20;
        case 2:
          return 50;
        case 3:
          return 80;
        case 4:
          return 100;
        default:
          return 0;
      }
    });

    const status = computed(() => {
      switch (props.step) {
        case 0:
          return $_trans("indumeter.stepsTitle.bottom");
        case 1:
          return $_trans("indumeter.stepsTitle.belowAverage");
        case 2:
          return $_trans("indumeter.stepsTitle.average");
        case 3:
          return $_trans("indumeter.stepsTitle.aboveAverage");
        case 4:
          return $_trans("indumeter.stepsTitle.top");
        default:
          return $_trans("indumeter.stepsTitle.noData");
      }
    });

    const industryAverageFormat = computed(() => {
      switch (props.industryFormat) {
        case "PERCENT":
          return `${parseFloat((props.industryAverage * 100).toFixed(3))}%`;
        default:
          return props.industryAverage;
      }
    });

    const descriptionText = computed(() => {
      switch (props.step) {
        case 0:
          return $_trans(`indumeter.stepsDescription.${props.metricName}.bottom`);
        case 1:
          return $_trans(`indumeter.stepsDescription.${props.metricName}.belowAverage`);
        case 2:
          return $_trans(`indumeter.stepsDescription.${props.metricName}.average`);
        case 3:
          return $_trans(`indumeter.stepsDescription.${props.metricName}.aboveAverage`);
        case 4:
          return $_trans(`indumeter.stepsDescription.${props.metricName}.top`);
        default:
          return $_trans(`indumeter.stepsDescription.noData`);
      }
    });

    const industryAvgText = computed(() => {
      return $_trans("indumeter.industryAverage");
    });

    const improveButtonText = computed(() => {
      return $_trans("indumeter.improveButton");
    });

    const howWorksButton = computed(() => {
      return $_trans("indumeter.howItWorksButton");
    });

    function improve() {
      const link = document.createElement("a");
      link.href = $_trans("indumeter.improveLink");
      link.target = "_blank";
      link.click();
      eventGA("how_to_improve");
    }

    function getStepRange(step: number) {
      if (props.steps) {
        const stepRange = props.steps.find((element) => element.step === step);
        if (stepRange) {
          return stepRange;
        }
      }

      return {} as { step: number; minValue: number; maxValue: number };
    }

    function stepRangeFormat(step: number) {
      const stepRange = getStepRange(step);
      const isLowestRate = step === 0;
      const isHighestRate = step === 4;
      const metricName = window._trans._indumeter.stepTooltips[props.metricName] ?? "";
      const prefix = metricName ? `${metricName}: ` : "";
      const { above, below, from, to } = window._trans._indumeter;

      switch (props.industryFormat) {
        case "PERCENT":
          const getPercentValue = (value: number) => {
            return formatNumberV2(value / 100, {
              style: "percent",
            });
          };
          if (isHighestRate) {
            return props.isRateReversed
              ? `${prefix}${below} ${getPercentValue(stepRange.maxValue ?? 0)}`
              : `${prefix}${above} ${getPercentValue(stepRange.minValue ?? 0)}`;
          } else if (isLowestRate) {
            return props.isRateReversed
              ? `${prefix}${above} ${getPercentValue(stepRange.minValue ?? 0)}`
              : `${prefix}${below} ${getPercentValue(stepRange.maxValue ?? 0)}`;
          } else {
            return props.isRateReversed
              ? `${prefix}${from} ${getPercentValue(stepRange.maxValue ?? 0)} ${to} ${getPercentValue(
                  stepRange.minValue ?? 0
                )}`
              : `${prefix}${from} ${getPercentValue(stepRange.minValue ?? 0)} ${to} ${getPercentValue(
                  stepRange.maxValue ?? 0
                )}`;
          }
        default:
          if (isHighestRate) {
            return props.isRateReversed
              ? `${prefix}${below} ${(stepRange.maxValue ?? 0).toFixed(0)}`
              : `${prefix}${above} ${(stepRange.minValue ?? 0).toFixed(0)}`;
          } else if (isLowestRate) {
            return props.isRateReversed
              ? `${prefix}${above} ${(stepRange.minValue ?? 0).toFixed(0)}`
              : `${prefix}${below} ${(stepRange.maxValue ?? 0).toFixed(0)}`;
          } else {
            return props.isRateReversed
              ? `${prefix}${from} ${(stepRange.maxValue ?? 0).toFixed(0)} ${to} ${(stepRange.minValue ?? 0).toFixed(0)}`
              : `${prefix}${from} ${(stepRange.minValue ?? 0).toFixed(0)} ${to} ${(stepRange.maxValue ?? 0).toFixed(
                  0
                )}`;
          }
      }
    }

    function openHowWork() {
      const link = document.createElement("a");
      link.href = $_trans("indumeter.howItWorksLink");
      link.target = "_blank";
      link.click();
      eventGA("how_indumeter_works");
    }

    function eventGA(type: string) {
      switch (type) {
        case "how_to_improve":
          gtagSend({
            event: "button_click",
            component_type: "secondary",
            component_variant: "default",
            component_icon_name: "no_icon",
            component_state: "active",
            component_label: "edrone",
            functionality_group: "newsletter",
            functionality: "dashboard",
            view: "",
            text: `how_to_improve_${props.metricName}`,
          });
          break;
        case "how_indumeter_works":
          gtagSend({
            event: "button_icon_click",
            component_type: "",
            component_variant: "",
            component_icon_name: "question_mark",
            component_state: "active",
            component_label: "edrone",
            functionality_group: "newsletter",
            functionality: "dashboard",
            view: "",
            text: `${props.metricName}`,
          });
          break;
      }
    }

    return {
      status,
      percent,
      colorProgress,
      improve,
      industryAverageFormat,
      colorEmojiClass,
      descriptionText,
      industryAvgText,
      improveButtonText,
      stepRangeFormat,
      openHowWork,
      howWorksButton,
    };
  },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.EdroneMeterDetail {
  width: 352px;
}

.EdroneMeterDetail .circle {
  position: absolute !important;
  top: 25px !important;
  overflow: hidden;
  border: 0;
  cursor: default;
  border-radius: 0;
  width: 20rem !important;
  height: 19.25rem !important;
}

.EdroneMeterDetail canvas {
  position: relative;
  top: 70px;
}

.EdroneMeterDetail .EdroneBadgeStatus {
  width: 190px !important;
  text-wrap: wrap !important;
  text-align: center !important;
  height: auto !important;
}

.EdroneMeterDetail__container {
  min-height: 168px;
  width: 232px;
  z-index: 1;
}

.EdroneMeterDetail__status {
  width: 185px;
}

.EdroneMeterDetail__description {
  text-wrap: pretty;
}

.EdroneMeterDetail__howWork {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 10px;
  margin-right: 10px;
  font-size: 1rem;
  cursor: pointer;
}

.EdroneMeterDetail__emoji {
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  @apply tw:border-2 tw:border-default-100 tw:bg-default-000;
}

.EdroneMeterDetail__emoji img {
  width: 32px;
}

.EdroneMeterDetail__emoji--bottom {
  top: 165px;
  left: 12px;
  z-index: 10;
}

.EdroneMeterDetail__emoji--bottomColor {
  @apply tw:border-red-500 tw:bg-red-100;
}

.EdroneMeterDetail__emoji--belowAvg {
  top: 65px;
  left: 46px;
  z-index: 10;
}

.EdroneMeterDetail__emoji--belowAvgColor {
  @apply tw:border-yellow-500 tw:bg-yellow-100;
}

.EdroneMeterDetail__emoji--average {
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.EdroneMeterDetail__emoji--averageColor {
  @apply tw:border-green-500 tw:bg-green-100;
}

.EdroneMeterDetail__emoji--overAvg {
  top: 65px;
  right: 46px;
  z-index: 10;
}

.EdroneMeterDetail__emoji--overAvgColor {
  @apply tw:border-green-500 tw:bg-green-100;
}

.EdroneMeterDetail__emoji--top {
  top: 165px;
  right: 12px;
  z-index: 10;
}

.EdroneMeterDetail__emoji--topColor {
  @apply tw:border-green-500 tw:bg-green-100;
}

.EdroneMeterDetail__emoji--noActive {
  @apply tw:border-default-100 tw:bg-default-000;
}

.EdroneMeterDetail__emoji--noActive img {
  opacity: 0.5;
}

.EdroneMeterDetail__emoji--noData {
  @apply tw:!border-blue-100 tw:!bg-blue-000;
}

.EdroneMeterDetail__emoji--noData img {
  opacity: 1;
}
</style>
